/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  assign,
  createMachine,
  Interpreter,
  sendParent,
  StateNode,
} from 'xstate-v4'
import { Image } from '../domain/types'
import sentryCapture from '~/utility/sentryCapture'

const IMAGE_MINIMUM_LONG_EDGE = 1800
const IMAGE_MINIMUM_SHORT_EDGE = 1350
const IMAGE_MAXIMUM_FILE_SIZE_IN_BYTES = Number(
  process.env.CLOUDINARY_MAXIMUM_FILE_SIZE_IN_BYTES || String(40 * 1024 ** 2),
)
const UPDATE_REPEATS = 5

// tslint:disable-next-line: no-empty-interface
interface UploadedImagePreviewContext {
  image: Image
  campsiteSlug: string
  id: string | number
  token: string
  caption: string
  updateRepeats: number
  status: string
}

type UploadedImagePreviewEvents =
  | { type: 'REMOVE_IMAGE' }
  | { type: 'UPDATE_CAPTION'; caption: string }

export function returnUploadedImagePreviewMachine() {
  return createMachine<UploadedImagePreviewContext, UploadedImagePreviewEvents>(
    {
      id: 'uploadedImagePreview',
      initial: 'verifyingImage',
      on: {
        REMOVE_IMAGE: [
          {
            target: 'removing',
            cond: ({ image }) => image.status !== 'invalid',
          },
          {
            target: 'removed',
          },
        ],
        UPDATE_CAPTION: {
          target: 'debounceCaptionAdding',
          actions: assign({
            caption: (_, { caption }) => caption,
          }),
        },
      },
      states: {
        idle: {
          tags: ['form'],
        },
        debounceCaptionAdding: {
          tags: ['form'],
          after: {
            500: {
              target: 'captionAdding',
            },
          },
        },
        verifyingImage: {
          always: [
            {
              target: 'dimensionsInvalid',
              cond: 'invalidImageDimensions',
            },
            {
              target: 'fileSizeInvalid',
              cond: 'invalidImageDimensions',
            },
            {
              target: 'statusInvalid',
              cond: 'invalidStatus',
              actions: (_: unknown, event: any) => {
                event.data.name = 'Uploaded image preview: image invalid status'
                handleInvalidImage(event)
              },
            },
            {
              target: 'idInvalid',
              cond: 'invalidId',
              actions: (_: unknown, event: any) => {
                event.data.name = 'Uploaded image preview: image invalid id'
                handleInvalidImage(event)
              },
            },
            {
              target: 'checkingIfCaptionSaved',
            },
          ],
        },
        valid: {
          tags: ['form'],
        },
        captionInvalid: {
          tags: ['form'],
        },
        dimensionsInvalid: {
          tags: ['imageInvalid', 'dimensionsInvalid'],
        },
        fileSizeInvalid: {
          tags: ['imageInvalid', 'fileSizeInvalid'],
        },
        statusInvalid: {
          tags: ['imageInvalid', 'statusInvalid'],
        },
        idInvalid: {
          tags: ['imageInvalid', 'idInvalid'],
        },
        removing: {
          invoke: {
            src: 'removeImage',
            onDone: 'removed',
            onError: 'error',
          },
        },
        removed: {
          entry: [
            sendParent(({ image }) => ({
              type: 'REMOVE',
              imagePreviewId: image.id,
            })),
            sendParent(() => ({
              type: 'IMAGE_UPDATED',
            })),
          ],
          type: 'final',
        },
        error: {
          entry: 'logError',
        },
        captionAdding: {
          tags: ['form'],
          always: [
            {
              target: 'captionAdded',
              cond: ({ caption }) => !!caption,
            },
            { target: 'captionInvalid' },
          ],
        },
        captionAdded: {
          tags: ['form'],
          entry: [
            assign({
              image: ({ image, caption }) => ({ ...image, caption }),
            }),
          ],
          invoke: {
            src: 'updateImage',
            onDone: {
              target: 'checkingIfCaptionSaved',
            },
            onError: 'settingCaptionError',
          },
        },
        settingCaptionError: {
          tags: ['form'],
        },
        checkingIfCaptionSaved: {
          tags: ['form'],
          invoke: {
            src: 'getImageStatus',
            onDone: [
              {
                target: 'valid',
                cond: ({ caption }, { data }: any) => {
                  const serverCaption = (data && (data.caption ?? '')) as string
                  const localCaption = caption ?? ''
                  // Treat null and empty string as equal; if equal, no need to update
                  return serverCaption === localCaption
                },
              },
              {
                // Backend change might not be visible immediately; retry saving caption
                // Only attempt updates when there is a non-empty local caption
                target: 'captionAdded',
                cond: ({ caption, updateRepeats }, { data }: any) => {
                  const serverCaption = (data && (data.caption ?? '')) as string
                  const localCaption = caption ?? ''
                  return (
                    !!localCaption &&
                    serverCaption !== localCaption &&
                    updateRepeats <= UPDATE_REPEATS
                  )
                },
                actions: assign({
                  updateRepeats: ({ updateRepeats }) => {
                    return updateRepeats + 1
                  },
                }),
              },
              { target: 'settingCaptionError' },
            ],
            onError: 'gettingStatusError',
          },
          exit: sendParent(() => ({
            type: 'IMAGE_UPDATED',
          })),
        },
        gettingStatusError: {
          tags: ['form'],
        },
      },
    },
    {
      actions: {
        logError: () => {
          throw new Error(
            'uploadedImagePreviewMachine logError not implemented',
          )
        },
      },
      guards: {
        invalidImageDimensions: ({ image }) => {
          const invalidPortrait =
            image.width < IMAGE_MINIMUM_SHORT_EDGE ||
            image.height < IMAGE_MINIMUM_LONG_EDGE
          const invalidLandscape =
            image.width < IMAGE_MINIMUM_LONG_EDGE ||
            image.height < IMAGE_MINIMUM_SHORT_EDGE
          return invalidPortrait && invalidLandscape
        },
        invalidImageFileSize: ({ image }) =>
          image.size >= IMAGE_MAXIMUM_FILE_SIZE_IN_BYTES,
        invalidStatus: ({ image }) => image.status === 'invalid',
        invalidId: ({ image }) =>
          typeof image.id === 'string' && !/^\d+$/.test(image.id),
      },
    },
  )
}

function handleInvalidImage(event: any) {
  sentryCapture(event.data)
}

export type UploadedImagePreviewStateMachineWithConfig = StateNode<
  UploadedImagePreviewContext,
  any,
  UploadedImagePreviewEvents
>

export type UploadedImagePreviewStateMachineInterpreter = Interpreter<
  UploadedImagePreviewContext,
  any,
  UploadedImagePreviewEvents
>
